import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  MoreVertical,
  CheckCircle2,
  Users,
  Eye,
  Pencil,
  Trash2,
  LayoutDashboard,
  ChevronUp,
  ChevronDown,
  IndianRupee,
  Clock,
  SlidersHorizontal as Filters,
  X,
  Calendar
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import AdvanceRequestDrawer from './AdvanceRequestDrawer';
import ApprovalModal from './ApprovalModal';
import styles from './AdvanceRequests.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';
import api from '../../../api/axios';
import Pagination from '../../../components/Common/Pagination/Pagination';
import NoData from '../../../components/Common/NoData/NoData';
import { useLoading } from '../../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const AdvanceRequests = () => {
  const navigate = useNavigate();
  const { loading } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });

  const [filters, setFilters] = useState({
    employee_id: '',
    status: ''
  });

  const [filterData, setFilterData] = useState({
    employees: []
  });

  const [requests, setRequests] = useState([]);
  const [metrics, setMetrics] = useState([
    { label: 'Pending Approval', value: '0', icon: <Clock size={22} />, theme: 'orange', highlight: true },
    { label: 'Total Approved', value: '₹0', icon: <CheckCircle2 size={22} />, theme: 'green' },
    { label: 'Monthly Payout', value: '₹0', icon: <IndianRupee size={22} />, theme: 'blue' },
    { label: 'Total Requests', value: '0', icon: <Users size={22} />, theme: 'purple' }
  ]);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'partially_approved', label: 'Partially Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const fetchFilterData = async () => {
    try {
      const response = await api.get('/employees-dropdown');
      setFilterData({
        employees: response.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/advance-requests/summary');
      if (response.data.success) {
        const data = response.data.data;
        setMetrics([
          { label: 'Pending Approval', value: (data.pending || '0').toString(), icon: <Clock size={22} />, theme: 'orange', highlight: true },
          { label: 'Total Approved', value: `₹${parseFloat(data.total_approved_amount || 0).toLocaleString('en-IN')}`, icon: <CheckCircle2 size={22} />, theme: 'green' },
          { label: 'Monthly Payout', value: `₹${parseFloat(data.total_requested_amount || 0).toLocaleString('en-IN')}`, icon: <IndianRupee size={22} />, theme: 'blue' },
          { label: 'Total Requests', value: (data.total_requests || '0').toString(), icon: <Users size={22} />, theme: 'purple' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await api.get('/advance-requests', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          employee_id: filters.employee_id,
          status: filters.status
        }
      });
      if (response.data.success) {
        setRequests(response.data.data.data || response.data.data || []);
        setPaginationData({
          total: response.data.data.total || 0,
          current_page: response.data.data.current_page || 1,
          per_page: response.data.data.per_page || 10,
          last_page: response.data.data.last_page || 1
        });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      if (error.response?.status === 404) {
        setRequests([]);
        setPaginationData(prev => ({ ...prev, total: 0, last_page: 1 }));
      } else {
        toast.error('Failed to load advance requests');
      }
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchSummary();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      employee_id: '',
      status: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setActiveDropdown(null);
      }
    };

    const handleScroll = () => {
      if (activeDropdown !== null) setActiveDropdown(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeDropdown]);

  const toggleActionMenu = (id, e) => {
    e.stopPropagation();
    if (activeDropdown === id) {
      setActiveDropdown(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 160
      });
      setActiveDropdown(id);
    }
  };

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    setIsDrawerOpen(true);
    setActiveDropdown(null);
  };

  const handleDelete = (id, name) => {
    setActiveDropdown(null);
    MySwal.fire({
      title: 'Delete Request?',
      text: `Are you sure you want to delete the advance request for ${name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/advance-requests/${id}`);
          toast.success('Request deleted');
          fetchRequests();
          fetchSummary();
        } catch (error) {
          toast.error('Failed to delete request');
        }
      }
    });
  };

  return (
    <div className={styles['advance-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} onClick={() => setActiveDropdown(null)}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Advance Requests', path: '/employees/advance-requests' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Advance Requests</h1>
              <p>Manage and track employee salary advance applications.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => { setSelectedRecord(null); setIsDrawerOpen(true); }}>
              <Plus size={20} />
              <span>Apply Advance</span>
            </button>
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[0]?.value} Pending · {metrics[1]?.value} Approved
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                  {m.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search requests..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowFilters(!showFilters); }}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.employee_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']} onClick={(e) => e.stopPropagation()}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Advance Requests</h3>
                  <p>Filter records by employee or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Employee</label>
                  <CustomSelect
                    value={filters.employee_id ? {
                      value: filters.employee_id,
                      label: filterData.employees.find(emp => emp.id === filters.employee_id)?.employee_name
                    } : null}
                    onChange={(opt) => handleFilterChange('employee_id', opt ? opt.value : '')}
                    options={filterData.employees.map(emp => ({ value: emp.id, label: emp.employee_name }))}
                    placeholder="All Employees"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: statusOptions.find(opt => opt.value === filters.status)?.label
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={statusOptions.filter(opt => opt.value !== '')}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={[styles['custom-table-container'], 'card'].join(' ')}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>EMPLOYEE</th>
                    <th>REQUESTED</th>
                    <th>APPROVED</th>
                    <th>STATUS</th>
                    <th>APPROVED BY</th>
                    <th>DATE</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length > 0 ? (
                    requests.map((request, index) => (
                      <tr key={request.id}>
                        <td>
                          <div className={styles['user-identity']}>
                            <div className={styles['avatar-small']}>
                              {(request.employee_name || request.employee?.name || 'E').charAt(0)}
                            </div>
                            <div className={styles['user-info']}>
                              <span 
                                className={[styles['user-name'], styles['clickable']].join(' ')} 
                                onClick={() => navigate(`/employees/advance-requests/view/${request.id}`)}
                              >
                                {request.employee_name || request.employee?.name || '---'}
                              </span>
                              <span className={styles['emp-id']}>ID: {request.employee_id}</span>
                            </div>
                          </div>
                        </td>
                        <td className={styles['amount-cell']}>₹{parseFloat(request.requested_amount || 0).toLocaleString('en-IN')}</td>
                        <td className={[styles['amount-cell'], styles['approved-amount']].join(' ')}>
                          {parseFloat(request.approved_amount || 0) > 0 ? `₹${parseFloat(request.approved_amount).toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td>
                          <span className={`${styles['status-badge']} ${styles[`status-${request.status?.toLowerCase()}`]}`}>
                            {request.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td>{request.approver_name || request.approved_by || '-'}</td>
                        <td className={styles['date-cell']}>{request.request_date || request.date}</td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${activeDropdown === request.id ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(request.id, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {activeDropdown === request.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/advance-requests/view/${request.id}`)}>
                                  <Eye size={16} /> View
                                </button>
                                {request.status === 'pending' && (
                                  <button className={styles['dropdown-item']} style={{ color: '#059669' }} onClick={(e) => { e.stopPropagation(); setSelectedRecord(request); setIsApprovalModalOpen(true); setActiveDropdown(null); }}>
                                    <CheckCircle2 size={16} /> Process Request
                                  </button>
                                )}
                                <button className={styles['dropdown-item']} onClick={() => handleEditClick(request)}>
                                  <Pencil size={16} /> Edit
                                </button>
                                <div className={styles['dropdown-divider']}></div>
                                <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDelete(request.id, request.employee_name || request.employee?.name)}>
                                  <Trash2 size={16} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : !loading && (
                    <NoData message="No Advance Requests found." isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['po-cards']}>
              {requests.length > 0 ? (
                requests.map((request) => (
                  <div key={request.id} className={styles['po-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>REQ-{request.id}</span>
                        <span className={styles['card-amount']}>₹{parseFloat(request.requested_amount || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={styles['action-btn']}
                          onClick={(e) => toggleActionMenu(request.id, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Employee</span>
                        <span className={styles['detail-value']}>{request.employee_name || request.employee?.name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Status</span>
                        <span className={`${styles['status-badge']} ${styles[`status-${request.status?.toLowerCase()}`]}`} style={{ minWidth: 'auto', fontSize: '10px', padding: '4px 8px' }}>
                          {request.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Approved Amt</span>
                        <span className={`${styles['detail-value']} ${styles['approved-amount']}`}>
                          ₹{parseFloat(request.approved_amount || 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} />
                        <span>{request.request_date || request.date}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : !loading && (
                <NoData message="No Advance Requests found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => { setCurrentPage(page); }}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <Footer />
        </main>
      </div>

      <AdvanceRequestDrawer 
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchRequests();
          fetchSummary();
        }}
        record={selectedRecord}
      />

      <ApprovalModal 
        isOpen={isApprovalModalOpen}
        onClose={() => setIsApprovalModalOpen(false)}
        record={selectedRecord}
        onSuccess={() => {
          fetchRequests();
          fetchSummary();
        }}
      />
    </div>
  );
};

export default AdvanceRequests;
